import React, { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { SITEDETAIL } from "@/constants/testIds";

/**
 * Backfill FAQPage Q&A pairs onto existing articles using Claude Haiku 4.5.
 *
 * New articles get FAQs co-generated inside the main Sonnet rewrite (no extra
 * cost). This panel is for historical articles that were rewritten before that
 * change. Each generated FAQ pack runs ~$0.0001 in tokens; the panel surfaces
 * candidate count + total spend per batch.
 */
export default function FaqBackfillPanel({ site }) {
  const [busy, setBusy] = useState(null); // 'dry' | 'run'
  const [flash, setFlash] = useState(null);
  const [result, setResult] = useState(null);

  async function trigger(dryRun) {
    if (busy) return;
    setBusy(dryRun ? "dry" : "run");
    setFlash(null);
    try {
      const r = await api.post(`/sites/${site.id}/articles/faq-backfill`, {
        dry_run: dryRun,
        limit: 25,
      });
      setResult(r.data);
      if (dryRun) {
        setFlash({ kind: "ok", msg: `${r.data.candidate_count} article(s) need FAQ backfill (showing up to 25 per batch)` });
      } else {
        setFlash({ kind: "ok", msg: `Generated FAQs for ${r.data.updated_count} article(s) — $${r.data.total_cost_usd.toFixed(4)} spent` });
      }
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "FAQ backfill failed" });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="border border-zinc-800 rounded-sm p-5 bg-zinc-900/30 space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="font-display text-lg font-bold">FAQ Schema Backfill</div>
          <div className="text-xs text-zinc-500 mono mt-1">
            Generates 3 Q&amp;A pairs per article via Claude Haiku 4.5 and emits them as <span className="text-zinc-300">FAQPage</span> JSON-LD for rich-snippet eligibility.
          </div>
        </div>
      </div>

      <div className="text-xs text-zinc-400 leading-relaxed">
        New articles already get FAQs from the Sonnet rewrite for free. This tool is for backfilling articles published <em>before</em> that change. Each batch processes up to 25 articles in parallel (4 concurrent Haiku calls) — re-run to drain more.
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          data-testid={SITEDETAIL.faqBackfillDryRun}
          onClick={() => trigger(true)}
          disabled={!!busy}
          className="border border-zinc-700 hover:border-zinc-500 text-zinc-200 px-3 py-1.5 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
        >
          {busy === "dry" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Count candidates
        </button>
        <button
          data-testid={SITEDETAIL.faqBackfillRun}
          onClick={() => trigger(false)}
          disabled={!!busy || (result && result.candidate_count === 0)}
          className="bg-amber-500/20 border border-amber-500/40 hover:bg-amber-500/30 text-amber-200 px-3 py-1.5 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
          title="Run Haiku on up to 25 articles missing FAQs"
        >
          {busy === "run" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Backfill 25 articles
        </button>
      </div>

      {result && (
        <div className="mt-3 border border-zinc-800 rounded-sm overflow-hidden">
          <div className="px-3 py-2 bg-zinc-900/60 text-[11px] text-zinc-400 mono border-b border-zinc-800 flex items-center justify-between">
            <span>{result.dry_run ? "DRY-RUN" : "BACKFILL COMPLETE"}</span>
            <span>
              {result.dry_run
                ? `${result.candidate_count} candidate${result.candidate_count === 1 ? "" : "s"}`
                : `+${result.updated_count} updated · $${result.total_cost_usd.toFixed(4)}`}
            </span>
          </div>
          {result.samples?.length > 0 ? (
            <div className="max-h-72 overflow-auto divide-y divide-zinc-800">
              {result.samples.map((s) => (
                <div key={s.article_id} className="px-3 py-2 text-xs">
                  <div className="text-zinc-300 truncate">{s.title}</div>
                  {!result.dry_run && (
                    <div className="mt-0.5 mono text-[10px] text-emerald-400">+ {s.faq_count} FAQs</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="px-3 py-3 text-xs text-zinc-500 mono">All articles already have FAQs. </div>
          )}
        </div>
      )}

      {flash && (
        <div
          data-testid="sitedetail-faq-backfill-flash"
          className={`text-xs mono ${flash.kind === "ok" ? "text-emerald-400" : "text-rose-400"}`}
        >
          {flash.kind === "ok" ? "✓ " : "✕ "}{flash.msg}
        </div>
      )}
    </div>
  );
}
